/**
 * Shared palette-index drawing kit for actor sprites (characters and
 * creatures). All helpers are size-agnostic: dimensions come from the grid.
 *
 * House style (docs/art-direction-rules.md §0): flat colour fields, one Ink
 * contour around the composed silhouette, three values per material, contact
 * shadow in Dark grass (13). Soft-gradient helpers are deliberately absent.
 */

const INK = 1;
const SHADOW = 13;
const NEIGHBORS = [[0, -1], [1, 0], [0, 1], [-1, 0], [1, 1], [1, -1], [-1, 1], [-1, -1]];

export function canvas(width, height) {
  return Array.from({ length: height }, () => Array(width).fill(0));
}

export function put(pixels, x, y, color) {
  const row = pixels[y];
  if (!row || x < 0 || x >= row.length || color === undefined || color === null) return;
  row[x] = color;
}

export function fillRect(pixels, x, y, width, height, color) {
  for (let py = y; py < y + height; py++) {
    for (let px = x; px < x + width; px++) put(pixels, px, py, color);
  }
}

export function hLine(pixels, x0, x1, y, color) {
  for (let x = x0; x <= x1; x++) put(pixels, x, y, color);
}

export function vLine(pixels, x, y0, y1, color) {
  for (let y = y0; y <= y1; y++) put(pixels, x, y, color);
}

export function fillEllipse(pixels, cx, cy, rx, ry, color) {
  const rx2 = Math.max(rx * rx, 0.01);
  const ry2 = Math.max(ry * ry, 0.01);
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) {
    for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
      if ((x - cx) ** 2 / rx2 + (y - cy) ** 2 / ry2 <= 1) put(pixels, x, y, color);
    }
  }
}

export function eraseEllipse(pixels, cx, cy, rx, ry) {
  fillEllipse(pixels, cx, cy, rx, ry, 0);
}

/**
 * Blit a string bitmap. `rows` is an array of equal-length strings; `legend`
 * maps each character to a palette index. '.' (or any unmapped char) skips.
 */
export function stamp(pixels, x, y, rows, legend) {
  for (let dy = 0; dy < rows.length; dy++) {
    const row = rows[dy];
    for (let dx = 0; dx < row.length; dx++) {
      const color = legend[row[dx]];
      if (color !== undefined) put(pixels, x + dx, y + dy, color);
    }
  }
}

/** Horizontally mirror a frame in place-safe copy (right = mirror of left). */
export function mirrorFrame(pixels) {
  return pixels.map((row) => [...row].reverse());
}

/**
 * One Ink ring around the composed opaque silhouette: writes ink into
 * transparent pixels 8-adjacent to opaque non-shadow pixels. Optional bounds
 * clamp the pass to one cell of a larger sheet.
 */
export function outerContour(pixels, { ink = INK, x0 = 0, y0 = 0, x1 = pixels[0].length - 1, y1 = pixels.length - 1 } = {}) {
  const isArt = (x, y) => {
    const v = pixels[y]?.[x];
    return v && v !== SHADOW && x >= x0 && x <= x1 && y >= y0 && y <= y1;
  };
  const edges = [];
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      if (pixels[y][x]) continue;
      if (NEIGHBORS.some(([dx, dy]) => isArt(x + dx, y + dy))) edges.push([x, y]);
    }
  }
  for (const [x, y] of edges) pixels[y][x] = ink;
}

/** Flat contact-shadow ellipse; writes only into transparent pixels. */
export function contactShadow(pixels, cx, cy, rx, ry, color = SHADOW) {
  const rx2 = Math.max(rx * rx, 0.01);
  const ry2 = Math.max(ry * ry, 0.01);
  for (let y = Math.max(0, Math.floor(cy - ry)); y <= Math.min(pixels.length - 1, Math.ceil(cy + ry)); y++) {
    for (let x = Math.max(0, Math.floor(cx - rx)); x <= Math.min(pixels[0].length - 1, Math.ceil(cx + rx)); x++) {
      if (pixels[y][x]) continue;
      if ((x - cx) ** 2 / rx2 + (y - cy) ** 2 / ry2 <= 1) pixels[y][x] = color;
    }
  }
}

/** 2px-wide creature leg with hoof cap. */
export function leg(pixels, x, top, footY, color, hoof) {
  fillRect(pixels, x, top, 2, Math.max(1, footY - top), color);
  put(pixels, x, footY, hoof);
  put(pixels, x + 1, footY, hoof);
}

/** 1px-wide creature leg with hoof cap. */
export function thinLeg(pixels, x, top, footY, color, hoof) {
  fillRect(pixels, x, top, 1, Math.max(1, footY - top), color);
  put(pixels, x, footY, hoof);
}

/** Quadruped stride offsets per walk frame; frames 0/2 are the neutral pose. */
export const WALK_GAIT = [
  { nearFront: 0, farFront: 0, nearBack: 0, farBack: 0 },
  { nearFront: 2, farFront: -1, nearBack: -2, farBack: 1 },
  { nearFront: 0, farFront: 0, nearBack: 0, farBack: 0 },
  { nearFront: -2, farFront: 1, nearBack: 2, farBack: -1 },
];

/** Humanoid arm/leg swing per walk frame: contact poses on 1 and 3. */
export function swingFor(frame) {
  return [0, 1, 0, -1][frame];
}

/** 1px body drop on contact frames; feet stay planted. */
export function bobFor(frame) {
  return [0, 1, 0, 1][frame];
}

export function walkSheet(drawFrame) {
  return ['down', 'up', 'left', 'right'].map((direction) =>
    [0, 1, 2, 3].map((frame) => drawFrame(direction, frame)));
}

/** Count of distinct opaque palette indices used in a frame. */
export function opaqueIndexCount(pixels) {
  const used = new Set();
  for (const row of pixels) for (const index of row) if (index) used.add(index);
  return used.size;
}
